import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { Helmet } from "react-helmet";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    minHeight: "100vh",
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },
  list: {
    paddingLeft: theme.spacing(3),
  },
  contact: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
}));

const PrivacyPolicy = () => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <Helmet>
        <title>Política de Privacidade - ChatFox</title>
      </Helmet>
      <Container maxWidth="md">
        <Paper className={classes.paper}>
          <Typography variant="h3" className={classes.title} align="center">
            Política de Privacidade
          </Typography>

          <Typography variant="body2" color="textSecondary" align="center" gutterBottom>
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </Typography>

          {/* 1. Controlador dos Dados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              1. Controlador dos Dados
            </Typography>
            <Typography variant="body1" paragraph>
              Esta Política de Privacidade se aplica ao aplicativo <strong>ChatFox</strong>, 
              uma plataforma de atendimento e automação via WhatsApp Business, Instagram Direct 
              e Facebook Messenger, operada por <strong>ChatFox Tecnologia</strong>.
            </Typography>
            <div className={classes.contact}>
              <Typography variant="body2">
                <strong>Contato:</strong> suporte@chatfox.com.br
              </Typography>
              <Typography variant="body2">
                <strong>País:</strong> Brasil
              </Typography>
            </div>
          </div>

          {/* 2. Dados Coletados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              2. Quais Dados São Coletados
            </Typography>
            <Typography variant="body1" paragraph>
              Coletamos os seguintes dados através das APIs oficiais da Meta (Facebook/WhatsApp/Instagram):
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Nome do usuário</Typography></li>
              <li><Typography variant="body1">Número de telefone (WhatsApp Business)</Typography></li>
              <li><Typography variant="body1">ID do usuário no Facebook/Instagram</Typography></li>
              <li><Typography variant="body1">Mensagens enviadas e recebidas (quando aplicável)</Typography></li>
              <li><Typography variant="body1">Imagens, vídeos e áudios enviados pelo usuário</Typography></li>
              <li><Typography variant="body1">Dados técnicos (endereço IP, logs de acesso, timestamps)</Typography></li>
              <li><Typography variant="body1">Informações de perfil público das redes sociais conectadas</Typography></li>
            </ul>
          </div>

          {/* 3. Como os Dados São Coletados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              3. Como os Dados São Coletados
            </Typography>
            <Typography variant="body1" paragraph>
              Os dados são coletados das seguintes formas:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Via APIs oficiais da Meta (WhatsApp Business API, Instagram Graph API, Facebook Graph API)</Typography></li>
              <li><Typography variant="body1">Quando o usuário final inicia uma conversa através dos canais conectados</Typography></li>
              <li><Typography variant="body1">Quando o cliente autoriza a conexão de sua conta WhatsApp Business, Instagram ou Facebook</Typography></li>
              <li><Typography variant="body1">Durante o processo de Embedded Signup do WhatsApp Business</Typography></li>
            </ul>
          </div>

          {/* 4. Finalidade do Uso dos Dados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              4. Para Que os Dados São Usados
            </Typography>
            <Typography variant="body1" paragraph>
              Os dados coletados são utilizados exclusivamente para as seguintes finalidades:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Permitir comunicação via WhatsApp Business, Instagram Direct e Facebook Messenger</Typography></li>
              <li><Typography variant="body1">Automatizar atendimento ao cliente através de chatbots e fluxos de conversa</Typography></li>
              <li><Typography variant="body1">Registrar e armazenar histórico de conversas para continuidade do atendimento</Typography></li>
              <li><Typography variant="body1">Enviar mensagens transacionais e templates aprovados pela Meta</Typography></li>
              <li><Typography variant="body1">Gerenciar filas de atendimento e distribuição de tickets</Typography></li>
              <li><Typography variant="body1">Gerar relatórios e métricas de atendimento para o cliente</Typography></li>
              <li><Typography variant="body1">Garantir a segurança e integridade da plataforma</Typography></li>
            </ul>
          </div>

          {/* 5. Compartilhamento de Dados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              5. Compartilhamento de Dados
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Os dados não são vendidos nem compartilhados com terceiros para fins comerciais.</strong>
            </Typography>
            <Typography variant="body1" paragraph>
              Os dados podem ser compartilhados apenas nas seguintes situações:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Com provedores de infraestrutura (servidores, banco de dados) necessários para operação técnica do serviço</Typography></li>
              <li><Typography variant="body1">Com a Meta (Facebook/WhatsApp/Instagram) através de suas APIs oficiais</Typography></li>
              <li><Typography variant="body1">Quando exigido por lei ou ordem judicial</Typography></li>
            </ul>
          </div>

          {/* 6. Armazenamento e Segurança */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              6. Armazenamento e Segurança
            </Typography>
            <Typography variant="body1" paragraph>
              Implementamos medidas de segurança para proteger os dados:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Criptografia em trânsito (HTTPS/TLS) para todas as comunicações</Typography></li>
              <li><Typography variant="body1">Controle de acesso baseado em perfis e permissões</Typography></li>
              <li><Typography variant="body1">Armazenamento em servidores seguros com backup regular</Typography></li>
              <li><Typography variant="body1">Logs de auditoria para rastreamento de acessos</Typography></li>
              <li><Typography variant="body1">Retenção limitada de dados conforme necessidade operacional</Typography></li>
            </ul>
          </div>

          {/* 7. Retenção e Exclusão de Dados */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              7. Retenção e Exclusão de Dados
            </Typography>
            <Typography variant="body1" paragraph>
              Os dados são armazenados pelo tempo necessário para prestação do serviço e cumprimento de obrigações legais.
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>O usuário pode solicitar a exclusão de seus dados a qualquer momento</strong> através do e-mail: 
              <strong> suporte@chatfox.com.br</strong>
            </Typography>
            <Typography variant="body1" paragraph>
              Após a solicitação, os dados serão excluídos em até 30 dias, exceto quando a retenção for 
              exigida por lei ou para exercício regular de direitos.
            </Typography>
          </div>

          {/* 8. Direitos do Usuário (LGPD/GDPR) */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              8. Direitos do Usuário (LGPD/GDPR)
            </Typography>
            <Typography variant="body1" paragraph>
              De acordo com a Lei Geral de Proteção de Dados (LGPD) e GDPR, você tem os seguintes direitos:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1"><strong>Acesso:</strong> Solicitar cópia dos seus dados pessoais</Typography></li>
              <li><Typography variant="body1"><strong>Correção:</strong> Solicitar correção de dados incorretos ou desatualizados</Typography></li>
              <li><Typography variant="body1"><strong>Exclusão:</strong> Solicitar a exclusão dos seus dados</Typography></li>
              <li><Typography variant="body1"><strong>Portabilidade:</strong> Solicitar transferência dos dados para outro fornecedor</Typography></li>
              <li><Typography variant="body1"><strong>Revogação de consentimento:</strong> Retirar o consentimento para tratamento de dados</Typography></li>
              <li><Typography variant="body1"><strong>Oposição:</strong> Opor-se ao tratamento de dados em determinadas situações</Typography></li>
              <li><Typography variant="body1"><strong>Informação:</strong> Obter informações sobre o tratamento dos seus dados</Typography></li>
            </ul>
            <Typography variant="body1" paragraph>
              Para exercer qualquer desses direitos, entre em contato através do e-mail: 
              <strong> suporte@chatfox.com.br</strong>
            </Typography>
          </div>

          {/* 9. Cookies e Tecnologias Similares */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              9. Cookies e Tecnologias Similares
            </Typography>
            <Typography variant="body1" paragraph>
              Utilizamos cookies e tecnologias similares para:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">Manter a sessão do usuário autenticado</Typography></li>
              <li><Typography variant="body1">Armazenar preferências de idioma e tema</Typography></li>
              <li><Typography variant="body1">Melhorar a experiência de navegação</Typography></li>
            </ul>
            <Typography variant="body1" paragraph>
              Você pode gerenciar ou desabilitar cookies através das configurações do seu navegador.
            </Typography>
          </div>

          {/* 10. Atualizações da Política */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              10. Atualizações da Política de Privacidade
            </Typography>
            <Typography variant="body1" paragraph>
              Esta Política de Privacidade pode ser atualizada periodicamente para refletir mudanças 
              em nossas práticas ou requisitos legais.
            </Typography>
            <Typography variant="body1" paragraph>
              Quando houver alterações significativas, notificaremos os usuários através:
            </Typography>
            <ul className={classes.list}>
              <li><Typography variant="body1">E-mail cadastrado na plataforma</Typography></li>
              <li><Typography variant="body1">Aviso na plataforma ao fazer login</Typography></li>
              <li><Typography variant="body1">Atualização da data no topo deste documento</Typography></li>
            </ul>
          </div>

          {/* 11. Legislação Aplicável */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              11. Legislação Aplicável
            </Typography>
            <Typography variant="body1" paragraph>
              Esta Política de Privacidade é regida pelas leis da República Federativa do Brasil, 
              em especial pela Lei Geral de Proteção de Dados (LGPD - Lei nº 13.709/2018).
            </Typography>
          </div>

          {/* Contato */}
          <div className={classes.section}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Dúvidas ou Solicitações
            </Typography>
            <Typography variant="body1" paragraph>
              Para qualquer dúvida sobre esta Política de Privacidade ou para exercer seus direitos, 
              entre em contato conosco:
            </Typography>
            <div className={classes.contact}>
              <Typography variant="body2">
                <strong>E-mail:</strong> suporte@chatfox.com.br
              </Typography>
              <Typography variant="body2">
                <strong>Plataforma:</strong> ChatFox - Atendimento Multicanal
              </Typography>
            </div>
          </div>
        </Paper>
      </Container>
    </div>
  );
};

export default PrivacyPolicy;
